import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaymentsIcon from '@mui/icons-material/Payments';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PageHeader from '../components/layout/PageHeader';
import TaskForm from '../components/tasks/TaskForm';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SummaryCard from '../components/dashboard/SummaryCard';
import BudgetMobileView from '../components/budget/BudgetMobileView';
import usePageTitle from '../hooks/usePageTitle';
import { Task, TaskFormData, getTasks, updateTask } from '../services/tasks';
import { Topic, getTopics } from '../services/topics';
import { Planner, getPlanners } from '../services/planners';
import { useCurrency } from '../context/CurrencyContext';

const CHART_COLORS = [
  '#B07A8F',
  '#C9A96E',
  '#6B9E78',
  '#6A8EB0',
  '#D4A84E',
  '#C26A6A',
  '#8B7EC8',
  '#5BA4B5',
];

type BudgetSortField = 'topic' | 'task' | 'expected' | 'actual' | 'paid' | 'remaining';
type SortDir = 'asc' | 'desc';

const BudgetPage: React.FC = () => {
  usePageTitle('Budget');
  const { symbol } = useCurrency();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tasks, setTasks] = useState<Task[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [planners, setPlanners] = useState<Planner[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  const [sortField, setSortField] = useState<BudgetSortField>('actual');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const loadTasks = useCallback(async () => {
    try {
      const res = await getTasks();
      setTasks(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [tRes, topRes, pRes] = await Promise.all([
          getTasks(),
          getTopics(),
          getPlanners(),
        ]);
        setTasks(tRes.data);
        setTopics(topRes.data);
        setPlanners(pRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async (data: TaskFormData) => {
    if (!editingTask) return;
    try {
      await updateTask(editingTask.id, data);
      setFormOpen(false);
      setEditingTask(null);
      loadTasks();
    } catch (err) {
      console.error(err);
    }
  };

  const handleRowClick = (task: Task) => {
    setEditingTask(task);
    setFormOpen(true);
  };

  const handleSort = (field: BudgetSortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'topic' || field === 'task' ? 'asc' : 'desc');
    }
  };

  if (loading) return <LoadingSpinner />;

  // Filter tasks with budget
  const budgetTasks = tasks.filter(
    (t) => (t.budget_expected != null && t.budget_expected > 0) || (t.budget_actual != null && t.budget_actual > 0)
  );

  // Build topic lookup map
  const topicMap = new Map<string, Topic>(topics.map((t) => [t.id, t]));

  // Group by topic for chart
  const topicTotals = new Map<string, number>();
  for (const task of budgetTasks) {
    const key = task.topic_id ?? '__uncategorized__';
    const amount = task.budget_actual ?? task.budget_expected ?? 0;
    topicTotals.set(key, (topicTotals.get(key) ?? 0) + amount);
  }

  const chartData = Array.from(topicTotals.entries())
    .map(([key, value]) => {
      const topic = key === '__uncategorized__' ? null : topicMap.get(key);
      const emoji = topic?.emoji ?? '';
      const name = topic ? `${emoji} ${topic.name}`.trim() : 'Uncategorized';
      return { name, value, topicId: key };
    })
    .sort((a, b) => b.value - a.value);

  // Summary totals
  const totalEffective = budgetTasks.reduce((s, t) => s + (t.budget_actual ?? t.budget_expected ?? 0), 0);
  const totalPaid = budgetTasks.reduce((s, t) => s + (t.budget_paid ?? 0), 0);
  const totalRemaining = budgetTasks.reduce((s, t) => {
    const effective = t.budget_actual ?? t.budget_expected ?? 0;
    return s + (effective - (t.budget_paid ?? 0));
  }, 0);

  const fmt = (n: number | null) => {
    if (n == null) return '—';
    return `${symbol}${n.toLocaleString()}`;
  };

  const barChartHeight = Math.min(Math.max(chartData.length * 36, 300), 600);

  return (
    <>
      <PageHeader title="Budget" />

      {isMobile ? (
        <BudgetMobileView
          tasks={budgetTasks}
          topics={topics}
          onTaskClick={handleRowClick}
        />
      ) : (
        <>
          {chartData.length > 0 && (
            <>
              {/* Desktop: three SummaryCards in a row */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 2,
                  mb: 3,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <SummaryCard
                    icon={<AccountBalanceWalletIcon />}
                    title="Total Budget"
                    value={fmt(totalEffective)}
                    color="#6A8EB0"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <SummaryCard
                    icon={<PaymentsIcon />}
                    title="Total Paid"
                    value={fmt(totalPaid)}
                    color="#6B9E78"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <SummaryCard
                    icon={<HourglassEmptyIcon />}
                    title="Remaining to pay"
                    value={fmt(totalRemaining)}
                    color="#C9A96E"
                  />
                </Box>
              </Box>

              {/* Horizontal bar chart */}
              <Card sx={{ mb: 3, p: 2 }}>
                <ResponsiveContainer width="100%" height={barChartHeight}>
                  <BarChart
                    layout="vertical"
                    data={chartData}
                    margin={{ top: 4, right: 80, left: 8, bottom: 4 }}
                  >
                    <XAxis type="number" hide />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={160}
                      tick={{ fontSize: 13 }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value: string) =>
                        value.length > 14 ? `${value.slice(0, 14)}…` : value
                      }
                    />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} isAnimationActive={false} minPointSize={6}>
                      {chartData.map((entry, index) => (
                        <Cell key={entry.topicId} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                      <LabelList
                        dataKey="value"
                        position="right"
                        formatter={(v: unknown) => `${symbol}${(v as number).toLocaleString()}`}
                        style={{ fontSize: 12, fill: '#666' }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </>
          )}

          {/* Desktop table */}
          <SortedTable
            budgetTasks={budgetTasks}
            topicMap={topicMap}
            isMobile={isMobile}
            sortField={sortField}
            sortDir={sortDir}
            handleSort={handleSort}
            handleRowClick={handleRowClick}
            fmt={fmt}
            symbol={symbol}
          />
        </>
      )}

      <TaskForm
        open={formOpen}
        task={editingTask}
        planners={planners}
        topics={topics}
        onSave={handleSave}
        onClose={() => { setFormOpen(false); setEditingTask(null); }}
        onDelete={() => { setFormOpen(false); setEditingTask(null); }}
      />
    </>
  );
};

interface SortedTableProps {
  budgetTasks: Task[];
  topicMap: Map<string, Topic>;
  isMobile: boolean;
  sortField: BudgetSortField;
  sortDir: SortDir;
  handleSort: (field: BudgetSortField) => void;
  handleRowClick: (task: Task) => void;
  fmt: (n: number | null) => string;
  symbol: string;
}

const SortedTable: React.FC<SortedTableProps> = ({
  budgetTasks,
  topicMap,
  isMobile,
  sortField,
  sortDir,
  handleSort,
  handleRowClick,
  fmt,
  symbol,
}) => {
  const sortedTasks = useMemo(() => {
    return [...budgetTasks].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'topic': {
          const nameA = a.topic_id ? (topicMap.get(a.topic_id)?.name ?? '') : '';
          const nameB = b.topic_id ? (topicMap.get(b.topic_id)?.name ?? '') : '';
          cmp = nameA.localeCompare(nameB);
          break;
        }
        case 'task':
          cmp = a.title.localeCompare(b.title);
          break;
        case 'expected':
          cmp = (a.budget_expected ?? 0) - (b.budget_expected ?? 0);
          break;
        case 'actual':
          cmp = (a.budget_actual ?? a.budget_expected ?? 0) - (b.budget_actual ?? b.budget_expected ?? 0);
          break;
        case 'paid':
          cmp = (a.budget_paid ?? 0) - (b.budget_paid ?? 0);
          break;
        case 'remaining': {
          const remA = (a.budget_actual ?? a.budget_expected ?? 0) - (a.budget_paid ?? 0);
          const remB = (b.budget_actual ?? b.budget_expected ?? 0) - (b.budget_paid ?? 0);
          cmp = remA - remB;
          break;
        }
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [budgetTasks, sortField, sortDir, topicMap]);

  return (
    <TableContainer
      sx={{
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: isMobile ? 1.5 : 3,
        bgcolor: 'background.paper',
        ...(!isMobile && { maxHeight: 'calc(100vh - 280px)', overflow: 'auto' }),
      }}
    >
      <Table size="small" sx={{ '& .MuiTableCell-root': { px: { xs: 1, sm: 1, md: 1.5 } } }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50', '& .MuiTableCell-root': { borderBottom: '2px solid', borderColor: 'grey.200' } }}>
            {isMobile ? (
              <>
                <TableCell sx={{ fontWeight: 600, position: 'sticky', top: 0, bgcolor: 'grey.50', zIndex: 1, width: 36, pl: 1.5 }}>Topic</TableCell>
                <TableCell sx={{ fontWeight: 600, position: 'sticky', top: 0, bgcolor: 'grey.50', zIndex: 1 }}>Task</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, position: 'sticky', top: 0, bgcolor: 'grey.50', zIndex: 1, whiteSpace: 'nowrap', pr: 1.5 }}>Total</TableCell>
              </>
            ) : (
              <>
                <TableCell sx={{ fontWeight: 600, position: 'sticky', top: 0, bgcolor: 'grey.50', zIndex: 1 }}>
                  <TableSortLabel active={sortField === 'topic'} direction={sortField === 'topic' ? sortDir : 'asc'} onClick={() => handleSort('topic')}>
                    Topic
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, position: 'sticky', top: 0, bgcolor: 'grey.50', zIndex: 1 }}>
                  <TableSortLabel active={sortField === 'task'} direction={sortField === 'task' ? sortDir : 'asc'} onClick={() => handleSort('task')}>
                    Task
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, position: 'sticky', top: 0, bgcolor: 'grey.50', zIndex: 1 }}>
                  <TableSortLabel active={sortField === 'expected'} direction={sortField === 'expected' ? sortDir : 'desc'} onClick={() => handleSort('expected')}>
                    Expected
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, position: 'sticky', top: 0, bgcolor: 'grey.50', zIndex: 1 }}>
                  <TableSortLabel active={sortField === 'actual'} direction={sortField === 'actual' ? sortDir : 'desc'} onClick={() => handleSort('actual')}>
                    Actual
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, position: 'sticky', top: 0, bgcolor: 'grey.50', zIndex: 1 }}>
                  <TableSortLabel active={sortField === 'paid'} direction={sortField === 'paid' ? sortDir : 'desc'} onClick={() => handleSort('paid')}>
                    Paid
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, position: 'sticky', top: 0, bgcolor: 'grey.50', zIndex: 1 }}>
                  <TableSortLabel active={sortField === 'remaining'} direction={sortField === 'remaining' ? sortDir : 'desc'} onClick={() => handleSort('remaining')}>
                    Remaining
                  </TableSortLabel>
                </TableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedTasks.length === 0 ? (
            <TableRow>
              <TableCell colSpan={isMobile ? 3 : 6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                No tasks with budget yet. Add budget information to tasks to see them here.
              </TableCell>
            </TableRow>
          ) : (
            sortedTasks.map((task) => {
              const topic = task.topic_id ? topicMap.get(task.topic_id) : null;
              const effective = task.budget_actual ?? task.budget_expected ?? 0;
              const remaining = effective - (task.budget_paid ?? 0);

              return (
                <TableRow
                  key={task.id}
                  hover
                  onClick={() => handleRowClick(task)}
                  sx={{ cursor: 'pointer' }}
                >
                  {isMobile ? (
                    <>
                      <TableCell sx={{ whiteSpace: 'nowrap', pr: 0.5, pl: 1.5 }}>
                        {topic ? (topic.emoji ?? '—') : '—'}
                      </TableCell>
                      {/* Fix 4 — dir="auto" so RTL Hebrew truncates on the correct end */}
                      <TableCell
                        dir="auto"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: 0,
                          width: '100%',
                        }}
                      >
                        {task.title}
                      </TableCell>
                      {/* Fix 5 — two-line Total / Paid stack */}
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', pr: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                          {symbol}{effective.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                          Paid: {symbol}{(task.budget_paid ?? 0).toLocaleString()}
                        </Typography>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {topic ? `${topic.emoji ?? ''} ${topic.name}`.trim() : '—'}
                      </TableCell>
                      <TableCell>{task.title}</TableCell>
                      <TableCell align="right">{task.budget_expected != null ? fmt(task.budget_expected) : '—'}</TableCell>
                      <TableCell align="right">{task.budget_actual != null ? fmt(task.budget_actual) : '—'}</TableCell>
                      <TableCell align="right">{task.budget_paid != null ? fmt(task.budget_paid) : '—'}</TableCell>
                      <TableCell align="right">{fmt(remaining)}</TableCell>
                    </>
                  )}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default BudgetPage;
